<template>
  <auth-guard
    route
  >
    <app-loading-indicator
      v-if="loading"
    />
    <div v-else-if="lot.id">
      <url-breadcrumbs
        v-if="lot"
        :items="[
          ['lot list', { name: 'lot-list' }],
          [`Lot #${lot.id}`, { name: 'lot' }]
        ]"
      />
      <base-title>
        Lot {{ lot.name }}
        <v-chip
          small
          :color="lot.is_nonconverter ? 'orange' : 'green'"
          class="ml-2 white--text"
        >
          {{ lot.is_nonconverter ? 'Non-Converter' : 'Converter' }}
        </v-chip>
        <template v-slot:subtitle>
          <div v-if="lot.parent_data">
            - <router-link :to="{ name: 'lot', params: { id: lot.parent_data.id } }">
              {{ lot.parent_data.name }}
            </router-link> <i>primary</i>
          </div>
          <div
            v-for="(child, index) in lot.children_data"
            :key="index"
          >
            - <router-link :to="{ name: 'lot', params: { id: child.id } }">
              {{ child.name }}
            </router-link> <i>sub</i>
            <br>
          </div>
          {{ lot.code }}<span v-if="lot.suffix"> / </span>{{ lot.suffix }}
          <br>
          <span>{{ lot.division_name }}</span>
        </template>
      </base-title>
      <div class="d-flex align-center my-4">
        <user-upload-dialog
          :baseurl="`/api/lots/${lot.id}/`"
          :file-links="fileLinks"
        />
        <auth-guard
          can="api.add_lot"
          type="hidden"
        >
          <v-btn
            color="primary"
            outlined
            small
            class="ml-3"
            @click="$router.push({ name: 'create-lot', query: { templateLotId: lot.id } })"
          >
            <v-icon
              small
              left
            >
              mdi-content-copy
            </v-icon>
            Copy Lot
          </v-btn>
          <v-btn
            v-if="!lot.parent && !lot.parent_data"
            color="primary"
            outlined
            small
            class="ml-3"
            @click="$router.push({ name: 'create-lot', query: { templateLotId: lot.id, parentLotId: lot.id } })"
          >
            <v-icon
              small
              left
            >
              mdi-source-branch-plus
            </v-icon>
            Add Sublot
          </v-btn>
        </auth-guard>
      </div>
      <lot-toolbar />
      <status-stepper
        v-if="stepperStatuses.length"
        :key="lot.status"
        :status="lot.status"
        :statuses="stepperStatuses"
      />
      <v-row>
        <v-col
          cols="12"
          md="6"
        >
          <lot-overview
            :lot="lot"
            :allocations="allocations"
            @updateLot="updateLot"
          />
          <auth-guard
            can="api.view_lot_designated_yards"
            type="hidden"
          >
            <designated-yards
              class="my-8"
              @fetchLot="fetchLot($route.params.id)"
              :yards="lot.yards"
              :url="lot.url"
            />
          </auth-guard>
          <auth-guard
            can="api.view_shipmentsummary"
            type="hidden"
          >
            <lot-settlement-overview
              class="my-8"
              :settlement="lot.shipment_summary_lot"
              :lot="lot"
              @fetchLot="fetchLot($route.params.id)"
            />
          </auth-guard>
          <auth-guard
            can="api.view_brokeredsettlement"
            type="hidden"
          >
            <brokered-overview
              v-if="lot.shipment_summary_lot && expectsSettlement(lot.designation)"
              class="mt-8"
              :yards="lot.yards"
              :lot="lot"
              @created="fetchLot($route.params.id)"
            />
          </auth-guard>
        </v-col>
        <v-col
          cols="12"
          md="6"
          flex-column
        >
          <auth-guard
            can="api.view_lot_designated_hedges"
            type="hidden"
          >
            <designated-hedges
              @fetchLot="fetchLot($route.params.id)"
              :hedges="lot.designated_hedges"
              :url="lot.url"
            />
          </auth-guard>
          <auth-guard
            can="api.view_lot_hedging_overview"
            type="hidden"
          >
            <hedging-overview
              class="my-8"
              :lot="lot"
              :final-toz="finalToz"
              :allocations="allocations"
              :allocation-totals="allocationTotals"
              :hedges="[]"
              :updating-lot="updatingLot"
              @updateLot="updateLot"
              @addedAllocation="fetchAllocations()"
            />
          </auth-guard>
          <auth-guard
            can="api.view_lot_hedging_overview"
            type="hidden"
          >
            <warehouse-manager-hedging-overview
              :lot="lot"
            />
          </auth-guard>
          <auth-guard
            can="api.view_inventory"
            type="hidden"
          >
            <inventory-overview
              class="mt-8"
              :inventory="lot.inventory"
              :lot-id="lot.id"
            />
          </auth-guard>
          <auth-guard
            can="api.view_lot_advance"
            type="hidden"
          >
            <advances-overview
              class="mt-8"
              :lot-id="lot.id"
            />
          </auth-guard>
        </v-col>
        <v-col>
          <auth-guard
            can="api.view_assayresult"
            type="hidden"
          >
            <assay-results
              :lot="lot"
            />
          </auth-guard>
        </v-col>
      </v-row>
      <v-row>
        <v-col>
          <auth-guard
            can="api.view_lot_purchases"
            type="hidden"
          >
            <intake-check-in-section
              :lot="lot"
            />
          </auth-guard>
        </v-col>
      </v-row>
      <v-row>
        <v-col>
          <auth-guard
            can="api.view_lot_purchases"
            type="hidden"
          >
            <intake-check-in-section-loosely-related
              :lot="lot"
            />
          </auth-guard>
        </v-col>
      </v-row>
      <v-row>
        <v-col
          cols="12"
        >
          <auth-guard
            can="api.view_lot_transits"
            type="hidden"
          >
            <transit-lot-table :lot-id="lot.id" />
          </auth-guard>
        </v-col>
      </v-row>
      <v-row>
        <v-col
          cols="12"
          md="6"
        >
          <auth-guard
            can="api.view_lot_purchases"
            type="hidden"
          >
            <lot-pieces-by-category :lot-id="lot.id" />
          </auth-guard>
        </v-col>
      </v-row>
      <auth-guard
        can="api.change_lot"
        type="hidden"
      >
        <v-btn
          color="primary"
          bottom
          right
          fixed
          fab
          @click="editDialog = !editDialog"
        >
          <v-icon>
            mdi-pencil-outline
          </v-icon>
        </v-btn>
        <edit-lot
          v-model="editDialog"
          :lot="lot"
        />
      </auth-guard>
    </div>
  </auth-guard>
</template>

<script>
import axios from 'axios'
import { mapGetters, mapActions } from 'vuex'
import HedgingOverview from './components/Hedging/HedgingOverview'
import WarehouseManagerHedgingOverview from './components/Hedging/WarehouseManagerHedgingOverview'
import LotOverview from './components/LotOverview'
import LotSettlementOverview from './components/Settlement/LotSettlementOverview'
import InventoryOverview from './components/InventoryOverview'
import StatusStepper from '@/base/components/StatusStepper'
import BrokeredOverview from './components/Brokered/BrokeredOverview'
import EditLot from './components/EditLot'
import IntakeCheckInSection from './components/IntakesCheckIns/IntakeCheckInSection'
import DesignatedHedges from './components/DesignatedHedges'
import DesignatedYards from './components/DesignatedYards'
import AssayResults from '@/views/Lots/components/AssayResult/AssayResults.vue'
import LotToolbar from '@/views/Lots/components/LotToolbar.vue'
import LotPiecesByCategory from '@/views/Lots/components/IntakesCheckIns/LotPieceByCategory.vue'
import UserUploadDialog from '@/base/components/UserUploadDialog.vue'
import fileLinks from '@/helpers/fileLinks'
import { expectsSettlement } from '@/helpers/utils'
import IntakeCheckInSectionLooselyRelated
  from '@/views/Lots/components/IntakesCheckIns/IntakeCheckInSectionLooselyRelated.vue'
import TransitLotTable from '@/views/Lots/components/TransitLotTable.vue'
import AdvancesOverview from './components/AdvancesOverview.vue'

export default {
  name: 'Lot',
  components: {
    AdvancesOverview,
    TransitLotTable,
    IntakeCheckInSectionLooselyRelated,
    UserUploadDialog,
    LotPiecesByCategory,
    LotToolbar,
    AssayResults,
    IntakeCheckInSection,
    EditLot,
    HedgingOverview,
    WarehouseManagerHedgingOverview,
    LotOverview,
    LotSettlementOverview,
    InventoryOverview,
    StatusStepper,
    BrokeredOverview,
    DesignatedHedges,
    DesignatedYards
  },
  data: () => ({
    editDialog: false,
    loading: true,
    updatingLot: false,
    finalToz: {},
    terms: null,
    showProfitEstimationDialog: false,
    allocations: [],
    allocationTotals: {}
  }),
  methods: {
    ...mapActions({
      fetchLot: 'lotStore/fetchLot',
      updatePurchaseLot: 'purchaseStore/updatePurchaseLot'
    }),
    async fetchAllocations () {
      if (!this.$store.getters.can('api.view_lot_allocation')) return
      const [allocResponse, totalsResponse] = await Promise.all([
        axios.get(`/api/lots/${this.$route.params.id}/allocations/`),
        axios.get(`/api/lots/${this.$route.params.id}/allocation_totals/`)
      ])
      this.allocations = allocResponse.data
      this.allocationTotals = totalsResponse.data
    },
    expectsSettlement,
    /**
     * Add a purchase to the current lot
     * @param {Object} purchase
     */
    // addPurchase (purchase) {
    //   const params = { purchases: [purchase], id: this.lot.id }
    //   this.updatePurchaseLot(params).then(() => {
    //     this.fetchLot(this.lot.id)
    //   })
    // },
    async updateLot (payload) {
      payload.id = this.$route.params.id
      await this.$store.dispatch('lotStore/updateLot', payload)
      await this.$store.dispatch('lotStore/fetchLot', this.$route.params.id)
    },
    /**
     * If a contained oz value is present use it rather than the estimated value
     * Used in the 'allocations' component in hedging overview
     * @return {{pt: methods.cPtToz, pd: methods.cPdToz, rh: methods.cRhToz}}
     */
    calcFinalOzMetal () {
      let pt = this.lot.estimated_pt
      let pd = this.lot.estimated_pd
      let rh = this.lot.estimated_rh
      if (this.lot.shipment_summary_lot) {
        pt = this.lot.shipment_summary_lot.contained_pt_toz
        pd = this.lot.shipment_summary_lot.contained_pd_toz
        rh = this.lot.shipment_summary_lot.contained_rh_toz
      }
      return { pt, pd, rh }
    },
    initialize () {
      return Promise.all([
        this.fetchLot(this.$route.params.id),
        this.fetchAllocations()
      ]).then(() => {
        this.yard = this.lot.yard
        this.finalToz = this.calcFinalOzMetal()
        this.loading = false
      })
    }
  },
  computed: {
    ...mapGetters({
      lot: 'lotStore/lot',
      lotTypes: 'lotStore/lotTypes',
      lotMaterialType: 'lotStore/lotMaterialType',
      lotStatuses: 'lotStore/lotStatuses',
      settlement: 'shipmentSummaryStore/brokeredSettlement'
    }),
    /**
     * Returns an empty array if in an ALTERNATE final state
     * @returns {Array}
     */
    stepperStatuses () {
      const alternateFinalStatuses = this.$store.getters['lotStore/finalStatuses'].filter(x => x !== 'CO')
      if (alternateFinalStatuses.includes(this.lot.status)) return []
      // return the normal list without the alternate final statuses
      return this.lotStatuses.filter(ls => !alternateFinalStatuses.includes(ls.value))
    },
    fileLinks () {
      return fileLinks.lotFileLink(this.lot)
    }
  },
  mounted () {
    this.initialize()
  }
}
</script>
